import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Heading1, Text } from '@lightspeed/flame/Text';
import { themeGet } from '@styled-system/theme-get';

import { Decimal } from '~/main/utils/Decimal';
import { ButtonBaseTile, ButtonBaseTileProps } from '~/renderer/components/UI/ButtonBaseTile';
import { useMoney } from '~/renderer/hooks/.';
import { CFD_MESSAGES } from '~/renderer/hooks/socket/types';
import { useSocket } from '~/renderer/hooks/socket/use-setup-socket';
import { CustomTipInput } from '~/renderer/modules/Tipping/TipInput/CustomTipInput';
import { ROUTE_TERMINAL_PAYMENT } from '~/renderer/routes';
import { ANALYTICS_EVENTS } from '~/renderer/store/middleware/types';
import { receiptActions } from '~/renderer/store/receipt';
import { getTip, getTipOptions, getTippableAmount } from '~/renderer/store/receipt/selectors';
import { track } from '~/renderer/utils/analytics';

import { TotalAmountBanner } from '../TotalAmountBanner';

const viewWidth = '75vw';
const maxViewWidth = 760;

const StyledFlexWrapper = styled(Flex)`
  background-color: ${themeGet('colors.black')};
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
`;

const StyledFlexProceedToPayment = styled(Flex)`
  width: ${viewWidth};
  max-width: ${`${maxViewWidth}px`};
  padding: ${themeGet('space.2')};
  padding-bottom: ${themeGet('space.10')};
  @media only screen and (max-height: ${themeGet('breakpoints.0')}) {
    padding: ${themeGet('space.1')};
  }
`;

const StyledFlexTipOptions = styled(Flex)`
  width: ${viewWidth};
  max-width: ${`${maxViewWidth}px`};
  flex-wrap: wrap;
`;

const StyledTipOptionButton = styled(ButtonBaseTile)<ButtonBaseTileProps>`
  margin: ${themeGet('space.2')};
  min-height: 108px;
  flex-grow: 1;
  align-items: center;
  justify-content: space-evenly;
  @media only screen and (max-height: ${themeGet('breakpoints.0')}) {
    margin: ${themeGet('space.1')};
  }
`;

export const StyledFullHeightFlex = styled(Flex)`
  height: 100vh;
`;

const AddTipHeader = styled(Heading1)`
  margin-bottom: ${themeGet('space.10')};
  @media only screen and (max-height: ${themeGet('breakpoints.0')}) {
    margin-bottom: ${themeGet('space.1')};
  }
`;
const calculateTipAmount = (amount: string | number, percentage: number): number => {
  const decimalAmount = new Decimal(amount).multipliedBy(percentage / 100);
  return decimalAmount.toNumber();
};

const TipInput: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { money, moneyParts } = useMoney();
  const dispatch = useDispatch();
  const tippableAmount = useSelector(getTippableAmount) || 0;
  const { enteredTip } = useSelector(getTip) || {};
  const tipOptions = useSelector(getTipOptions);
  const history = useHistory();
  const publish = useSocket();
  const maxLength: number = moneyParts(tippableAmount).formattedNumber.length;

  const handleUpdateEnteredTip = React.useCallback(
    async ({ id, value }: { id: string; value: number }): Promise<void> => {
      dispatch(
        receiptActions.updateEnteredTip({
          tipId: id,
          enteredTipAmount: calculateTipAmount(tippableAmount, value),
        }),
      );

      await track(ANALYTICS_EVENTS.TIP_OPTION, {
        tip_input: value > 0 ? 'prefilled' : 'no_tip',
      });
    },
    [dispatch, tippableAmount],
  );

  const handleProceedToPayment = React.useCallback(() => {
    publish(CFD_MESSAGES.TIP_CONFIRMED, enteredTip);
    dispatch(receiptActions.updatePaymentStaged());
    history.push(ROUTE_TERMINAL_PAYMENT);
  }, [publish, enteredTip, dispatch, history]);

  return (
    <StyledFullHeightFlex flexDirection="column">
      <TotalAmountBanner />
      <StyledFlexWrapper>
        <Flex flexDirection="column" alignItems="center" justifyContent="center" flexGrow={1}>
          <AddTipHeader role="heading" color="white">
            {t('receipt.tipping.addTipQuestion')}
          </AddTipHeader>
          <Box>
            <StyledFlexTipOptions>
              {tipOptions.map(({ value, id }) => (
                <StyledTipOptionButton
                  key={id}
                  aria-label={`${value.toString().toLowerCase().replace(' ', '-')}-button`}
                  onClick={async () => {
                    await handleUpdateEnteredTip({ id, value });
                  }}
                  toggled={id === enteredTip?.tipId}
                >
                  <Flex flexDirection="column" alignItems="center" justifyContent="center">
                    <Text size="xlarge">
                      {value === 0
                        ? t('receipt.tipping.noTip')
                        : t('receipt.tipping.percent', { percent: value.toString() })}
                    </Text>
                    {value !== 0 && (
                      <Text mt={1} size="large">
                        {money(calculateTipAmount(tippableAmount, value))}
                      </Text>
                    )}
                  </Flex>
                </StyledTipOptionButton>
              ))}
            </StyledFlexTipOptions>
          </Box>
          <StyledFlexProceedToPayment>
            <CustomTipInput maxLength={maxLength} />
          </StyledFlexProceedToPayment>
        </Flex>
        <StyledFlexProceedToPayment>
          <ButtonBaseTile
            aria-label="proceed-to-payment-button"
            onClick={handleProceedToPayment}
            fullWidth={true}
            disabled={!enteredTip?.tipId || enteredTip?.tipAmount === null}
          >
            <Text size="xlarge">{t('receipt.tipping.proceedToPayment')}</Text>
          </ButtonBaseTile>
        </StyledFlexProceedToPayment>
      </StyledFlexWrapper>
    </StyledFullHeightFlex>
  );
};

export { TipInput };
